import { useCallback, useEffect, useState } from 'react';

export type UseCountDownTimerProps = { timeInSeconds: number };

export const formatTimeLabel = (timeInSeconds: number): string => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export function useCountDownTimer({ timeInSeconds }: UseCountDownTimerProps) {
  const [secondsRemaining, setSecondsRemaining] = useState(timeInSeconds);
  const [label, setLabel] = useState<string>(formatTimeLabel(timeInSeconds));
  const [percentElapsed, setPercentElapsed] = useState<number>(100);
  const [hasExpired, setHasExpired] = useState<boolean>(false);

  // Function to reset the timer
  const resetTimer = useCallback(() => {
    setSecondsRemaining(timeInSeconds);
    setLabel(formatTimeLabel(timeInSeconds));
    setPercentElapsed(100);
    setHasExpired(false);
  }, [timeInSeconds]);

  // Reset the timer when timeInSeconds changes
  useEffect(() => resetTimer(), [resetTimer]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (secondsRemaining > 0) {
        const newTime = secondsRemaining - 1;
        setSecondsRemaining(newTime);
        setLabel(formatTimeLabel(newTime));
        setPercentElapsed((newTime / timeInSeconds) * 100);
      } else {
        setHasExpired(true);
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [timeInSeconds, secondsRemaining]);

  return { label, percentElapsed, hasExpired, resetTimer };
}
